import { Accessor, ColorX, GFunc, LogicX, ZTime, ZValidate } from "IZOX/STATIC";
import { HStack, VStack, XButton, XDiv } from "IZOX/_X";
// eslint-disable-next-line no-unused-vars
import React, { CSSProperties, useCallback, useEffect, useRef, useState } from "react";
import QFText from "./QFText";
import { MdOutlineCalendarToday } from "react-icons/md";
import { Calendarizo } from "IZOX/BLOCKS/Labs";
import _ from "lodash";

/**
 * @typedef {import("IZOX/_X/XInput").xinputparams} xinputparams
 * @typedef {import("IZOX/_X/XInput").xinputstyleparams} xinputstyleparams
 * @typedef {import("IZOX/STATIC/ZValidate").fvalidate} fvalidate
 * @typedef {import("IZOX/__Typedef/common").lang} lang
 * 
 * @typedef {Object} qfdaterangeparamsaddOns
 * @property {String} name
 * @property {lang} label
 * @property {any} defaultValue
 * @property {Function} _onChange
 * @property {Function} _onSetDefault
 * @property {any} formValue
 * @property {String} error_bColor
 * @property {String} invalidColor
 * @property {Number} calendarSize
 * @property {String} minDate
 * @property {String} maxDate
 * @property {String} minRange
 * @property {String} maxRange
 * @property {Number} defaultDisYear
 * @property {Number} defaultDisMonth
 * @property {({doc, field, addOns}) => Boolean} isError
 * @property {fvalidate | fvalidate[]} validators
 * @property {fvalidate | fvalidate[]} validators
 * @property {"year" | "month" | "date"} dateType
 * @property {Boolean} showMinute
 * @property {Boolean} showSecond
 * 
 * @typedef {qfdaterangeparamsaddOns & xinputparams & xinputstyleparams} qfdaterangeparams
 * 
 * @param {qfdaterangeparams} param0
 * @returns 
 */
function QFDateRange({
  name = undefined,
  label = undefined,
  _onChange = null,
  _onSetDefault = null,
  defaultValue = undefined,
  formValue = {},
  dateFormat = "DD/MM/YYYY",
  error_bColor = ColorX.CSS("Error"),
  invalidColor = ColorX.CSS("pureRed"),
  calendarSize = 30,
  minDate = undefined,
  maxDate = undefined,
  minRange = undefined,
  maxRange = undefined,
  disabled_bgColor = ColorX.CSS("bgGreyOut"),
  defaultDisYear = undefined,
  defaultDisMonth = undefined,
  isError = undefined,
  addOns = undefined,
  updateVia = undefined, 
  updateField = undefined,
  forceUpdateField = undefined,
  performanceBoost = undefined,
  dateType = "date",
  showMinute = true,
  showSecond = false,
  placeholder = undefined,
  validators = [],
  ...others
}){

  const ref = useRef(null);
  const [display, setDisplay] = useState(defaultValue);
  const [valid, setValid] = useState(false);

  const [, updateState] = React.useState();
  const forceUpdate = React.useCallback(() => updateState({}), []);

  const GetDefaultDateFormat = useCallback(() => {
    switch(dateType){
      case "year": return "YYYY";
      case "month": return "MMM YYYY";
      default: case "date": return "DD/MM/YYYY";
      case "datetime": return showSecond ? "DD/MM/YYYY HH:mm:ss" : "DD/MM/YYYY HH:mm";
      case "time": return showSecond ? "HH:mm:ss" : "HH:mm";
    }
  }, [dateType, showSecond]);

  const _modToOnChange = (name, {start, end}) => {
    let rtn = {start, end};
    if(minDate){
      if(ZTime.IsBefore(start, minDate)){
        rtn.start = minDate;
      }
      if(ZTime.IsBefore(end, minDate)){
        rtn.end = minDate;
      }
    }
    if(maxDate){
      if(ZTime.IsAfter(start, maxDate)){
        rtn.start = maxDate;
      }
      if(ZTime.IsAfter(end, maxDate)){
        rtn.end = maxDate;
      }
    }

    _onChange(name, rtn);
  };

  dateFormat = dateFormat ?? GetDefaultDateFormat();
  placeholder = placeholder ?? `${dateFormat} - ${dateFormat}`;

  useEffect(() => {
    if(defaultValue) {
      _onSetDefault?.(name, defaultValue);
      let disStr = "";
      try {
        let {start, end} = defaultValue ?? {};
        let disStrStart = ZTime.Moment(start).format(dateFormat);
        let disStrEnd = ZTime.Moment(end).format(dateFormat);
        let disStr = `${disStrStart} - ${disStrEnd}`;
        setDisplay(disStr);
        setValid(true);
      }catch{
        disStr = defaultValue;
        setValid(false);
      }
      setDisplay(disStr);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue]);

  useEffect(() => {
    let _value = Accessor.Get(formValue, name);
    if(_.isUndefined(_value)){
      setValid(true);
      setDisplay("");
      return;
    }
    let {start, end} = _value ?? {};
    if(_.isString(_value)){
      if(_value.length >= (2*dateFormat.length + 3)){
        let [start, end] = _value.split(" - ");
        let disStrStart = ZTime.Moment(start).format(dateFormat);
        let disStrEnd = ZTime.Moment(end).format(dateFormat);
        let disStr = `${disStrStart} - ${disStrEnd}`;
        setDisplay(disStr);
        setValid(true);
      }else{
        let _dateFormat = `${dateFormat} - ${dateFormat}`;
        if(/[^a-zA-Z]/.test(_dateFormat[_value.length])){
          if(_dateFormat[_value.length]===" "){
            _value += " - ";
          }else{
            _value += _dateFormat[_value.length];
          }
        }
        setDisplay(_value);
        setValid(false);
      }
    }else if(start && end){
      if(start.length >= dateFormat.length && end.length >= dateFormat.length){
        try {
          let disStrStart = ZTime.Moment(start).format(dateFormat);
          let disStrEnd = ZTime.Moment(end).format(dateFormat);
          let disStr = `${disStrStart} - ${disStrEnd}`;
          setDisplay(disStr);
          setValid(true);
        }catch{
          setDisplay(_value);
          setValid(false);
        }
      }else{
        if(/[^a-zA-Z]/.test(dateFormat[_value.length])){
          _value += dateFormat[_value.length];
        }
        setDisplay(_value);
        setValid(false);
      }
      
    }else{
      setDisplay("");
    }
  }, [display, formValue, name, dateFormat]);

  // eslint-disable-next-line no-unused-vars
  let {width, height = 30, bgColor, bW, bColor, fontSize, color, readOnly, p, ...exOthers} = others;

  let _value = Accessor.Get(formValue, name);
  let _error = !valid;
  if(isError){
    _error = _error || isError({doc: formValue, field: _value, addOns});
  }
  if(!_.isEmpty(validators)){
    _error = _error || !ZValidate.Check(_value, validators);
  }

  const onInputChange = (name, text) => {
    setDisplay(text);
    if(text.length >= (2*dateFormat.length + 3)){
      let [start, end] = text.split(" - ");
      if(ZTime.IsFormatValid(start, dateFormat)
        && ZTime.IsFormatValid(end, dateFormat)){
        _modToOnChange(name, {
          start: ZTime.Parse(start, dateFormat).toISOString(),
          end: ZTime.Parse(end, dateFormat).toISOString(),
        });
        setValid(true);
      }else{
        _onChange(name, text);
        setValid(false);
      }
    }else{
      _onChange(name, text);
      setValid(false);
    }
    forceUpdate?.();
  };

  const onDeleteInput = (text, e) => {
    e.preventDefault();
    let _dateFormat = `${dateFormat} - ${dateFormat}`;
    if(text.length === dateFormat.length + 3){
      text = text.slice(0, -4);
    }else if(!LogicX.isOneOf(_dateFormat[text.length - 1], ["H", "h", "m", "M", "D", "Y", "L"])){
      text = text.slice(0, -2);
    }else{
      text = text.slice(0, -1);
    }
    _onChange(name, text);
  };

  const onCalendarConfirm = ({start, end}) => {
    if(readOnly) return;
    let disStrStart = ZTime.Moment(start).format(dateFormat);
    let disStrEnd = ZTime.Moment(end).format(dateFormat);
    let disStr = `${disStrStart} - ${disStrEnd}`;
    setDisplay(disStr);
    _modToOnChange(name, {start, end});
    setValid(true);
    setTimeout(() => {
      GFunc.CloseSelectorOverlay();
    }, 100);
  };

  const openCalendar = (e) => {
    if(readOnly) return;
    let rect = ref.current.getBoundingClientRect();
    let {start, end} = _value ?? {};
    GFunc.SelectorOverlay(() => {
      return (
        <VStack position="absolute"
          style={{
            top: rect.top + 40,
            left: rect.left
          }}>
          <Calendarizo 
            range
            minDate={minDate}
            maxDate={maxDate}
            minRange={minRange}
            maxRange={maxRange}
            defaultDisMonth={defaultDisMonth}
            defaultDisYear={defaultDisYear}
            valStart={start}
            valEnd={end}
            size={calendarSize}
            directConfirm
            onConfirm={onCalendarConfirm}
            style={{borderRadius: 0, padding: 2}}
            />
        </VStack>
      );
    }, {}, true, false, true);
  };

  return (
    <XDiv width={width} position="relative" overflow="visible" 
      forwardRef={ref}>
      <HStack lockHeight={height} {...exOthers} bgColor={readOnly ? 
        (disabled_bgColor ?? bgColor ?? ColorX.CSS("pureWhite")):
        (bgColor ?? ColorX.CSS("pureWhite"))} bW={bW} bColor={_error ? error_bColor : bColor}>
        <QFText
          {...others}
          readOnly={readOnly}
          disabled_bgColor={disabled_bgColor}
          color={valid ? color : invalidColor}
          name={"display"}
          formValue={{display: display}}
          fontSize={fontSize}
          _onChange={(_, text) => onInputChange(name, text)}
          onDeleteKeyDown={(text, e) => onDeleteInput(text, e)}
          _onSetDefault={_onSetDefault}
          placeholder={placeholder}
          bW={0}
          />
        <XButton padding={5} lockWidth={height} lockHeight={height} onPress={openCalendar} disabled={readOnly}>
          <MdOutlineCalendarToday color={ColorX.CSS("greyOut")} size={fontSize + 5}/>
        </XButton>
      </HStack>
    </XDiv>
  );
}

export default QFDateRange;